import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AssetDoc,
  CustomerDoc,
  CustomerFormValues,
  CustomerType,
  Visibility,
  assetInDocumentSchema,
  imageFileSchema,
} from '@livekatsomo/models';
import {
  FileUploadProgess,
  UploadProgressFunction,
  ValidateCustomerSlugFunction,
} from '@livekatsomo/types';
import { useEnvironment } from '@livekatsomo/web/hooks';
import {
  ImageUploadButtonField,
  RadioField,
  SelectField,
  SlugField,
  TextField,
} from '@livekatsomo/web/ui-components/react-hook-form-components';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * The schema for the customer form data.
 */
const formSchema = z.object({
  name: z.string().nonempty('Name is required'),
  description: z.string(),
  slug: z.string().nonempty('Slug is required'),
  customerType: z.enum(['customer', 'shop']),
  logo: imageFileSchema.or(assetInDocumentSchema),
  banner: imageFileSchema.or(assetInDocumentSchema),
  poster: imageFileSchema.or(assetInDocumentSchema),
  visibility: z.enum(['private', 'public']),
});

/**
 * Props for the CustomerForm component.
 */
export interface CustomerFormProps {
  /**
   * An array of assets to be used in the form.
   */
  assets?: AssetDoc[];
  /**
   * A customer document to be edited, if any.
   */
  customer?: CustomerDoc;
  /**
   * A function to be called to close the dialog.
   */
  onClose: () => void;
  /**
   * A function to validate the customer slug.
   */
  onValidateSlug: ValidateCustomerSlugFunction;
  /**
   * A function to be called when the form is submitted.
   * @param customer - The customer form values.
   * @param setProgress - A function to set the upload progress.
   */
  onSubmit: (
    customer: CustomerFormValues,
    setProgress?: UploadProgressFunction,
  ) => Promise<void>;
}

/**
 * A form for adding or editing customer details.
 */
export function CustomerForm({
  assets,
  customer,
  onClose,
  onSubmit,
  onValidateSlug,
}: CustomerFormProps) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [uploadProgress, setProgress] = useState<FileUploadProgess>({});
  const { development } = useEnvironment();

  const logos =
    assets?.filter((asset) => asset.purpose?.includes('logo')) || [];
  const posters =
    assets?.filter((asset) => asset.purpose?.includes('poster')) || [];
  const banners =
    assets?.filter((asset) => asset.purpose?.includes('banner')) || [];

  const initialValues = useMemo(
    () => ({
      name: '',
      description: '',
      slug: '',
      visibility: 'private',
      logo: [] as File[],
      poster: [] as File[],
      banner: [] as File[],
      customerType: 'customer',
      ...customer,
    }),
    [customer],
  );

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isSubmitting, isValid },
    setValue,
    reset,
    handleSubmit,
  } = methods;

  const customerType = methods.watch('customerType');

  const handleValidateSlug = useCallback(
    (slug: string) => onValidateSlug(slug, customer),
    [onValidateSlug, customer],
  );

  const handleClose = () => {
    setErrorMessage(undefined);
    setProgress({});
    onClose();
  };

  const submit = async (values: typeof initialValues) => {
    console.log('customer form submitted', values);

    try {
      const validatedValues = await formSchema.parseAsync(values);
      await onSubmit(validatedValues, setProgress);
      handleClose();
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)} onReset={() => reset()}>
        {/* <DebugDetails data={{ ...values, errors, dirty, status }} /> */}
        <DevTool control={methods.control} />
        <DialogContent
          sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
        >
          <DialogContentText>
            {t('Add customer details here.')}
          </DialogContentText>
          {logos?.length ? (
            <SelectField
              options={logos}
              variant="standard"
              name="logo"
              label={t('Customer Logo')}
              optionViewer={(asset) => asset.filename}
            />
          ) : null}
          <ImageUploadButtonField
            uploadProgress={uploadProgress}
            name="logo"
            label={t('Upload Customer Logo')}
          />
          {assets?.length ? (
            <SelectField
              options={banners}
              name="banner"
              label={t('Banner image')}
              optionViewer={(asset) => asset.filename}
              variant="standard"
            />
          ) : null}
          <ImageUploadButtonField
            uploadProgress={uploadProgress}
            name="banner"
            label={t('Upload Banner')}
          />
          <TextField
            autoFocus
            margin="dense"
            name="name"
            required
            label={t('name')}
            type="text"
            fullWidth
            variant="standard"
          />
          <TextField
            margin="dense"
            name="description"
            label={t('description')}
            type="text"
            fullWidth
            variant="standard"
          />
          <SlugField
            name="slug"
            required
            margin="dense"
            label={t('slug')}
            type="text"
            source="name"
            validate={handleValidateSlug}
            fullWidth
            variant="standard"
          />
          <RadioField
            name="visibility"
            label={t('visibility')}
            options={['public', 'private'] as Visibility[]}
          />
          <RadioField
            name="customerType"
            label={t('type')}
            options={['customer', 'shop'] as CustomerType[]}
          />
          {customerType === 'shop' ? (
            <>
              <SelectField
                options={posters}
                name="poster"
                optionViewer={(asset) => asset.filename}
                label={t('Poster image')}
              />
              <ImageUploadButtonField
                uploadProgress={uploadProgress}
                name="poster"
                label={t('Upload Poster image')}
              />
            </>
          ) : null}
          {errorMessage ? (
            <Typography
              color="error"
              variant="body1"
              sx={{
                textAlign: 'center',
                mt: 2,
              }}
            >
              {errorMessage}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          {development ? (
            <Button
              type="button"
              variant="outlined"
              onClick={async (e) => {
                const generateCustomerFormContent = (
                  await import(
                    '@livekatsomo/web/ui-components/random-data-generators'
                  )
                ).generateCustomerFormContent;
                generateCustomerFormContent(setValue);
              }}
            >
              {t('Generate')}
            </Button>
          ) : null}
          <Button onClick={handleClose}>{t('Cancel')}</Button>
          <Button type="reset" disabled={isSubmitting || !isDirty}>
            {t('Reset')}
          </Button>
          <LoadingButton
            type="submit"
            disabled={!isValid || isSubmitting || !isDirty}
            loading={isSubmitting}
          >
            {customer ? t('Update') : t('Add')}
          </LoadingButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

export default CustomerForm;
